import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import { Box, Chip, Collapse, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { styled } from "@mui/system";
import type { DateTime } from "luxon";
import { type FC, useCallback, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useWeekViewContext } from "../../contexts/WeekView/WeekViewContext";
import { useExpandedGroups } from "../../hooks/useExpandedGroups";
import { useRevealWage } from "../../hooks/useRevealWage";
import { useDeleteDayOff } from "../../pages/Profile/v2/hooks";
import { formatHoursMinutes, formatPtoTimeRange, isFullStaffingDayPto } from "../../utils/dateUtils";
import type { DayOff, Nurse, SpecificPreference } from "../../utils/models";
import { daysOffAndPtoToNursePreferences, nursePreferencesToDaysOffAndPto } from "../../utils/utilFunctions";
import AddDayOffDialog from "../AddDayOffDialog/AddDayOffDialog";
import { ExternalTimeOffIndicator } from "../ExternalTimeOffIndicator/ExternalTimeOffIndicator";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import ColorfulRole from "../UIToolkit/ColorfulRole/ColorfulRole";
import { DeletePTODialog } from "./DeletePTODialog";
import { PTOMenu } from "./PTOMenu";
import { PtoRowActions } from "./PtoRowActions";
import { getStaffTableColumnWidths } from "./utils";

/** Group key for the day's time-off section — it is a single fixed group, not one per location. */
const PTO_GROUP_KEY = "pto";

const StyledTableRow = styled(TableRow)({
	"&:hover": {
		backgroundColor: "var(--inhouse-primary-alpha-08) !important",
		"& td": { backgroundColor: "var(--inhouse-primary-alpha-08) !important" },
		"& .delete-button": { opacity: 0 },
	},
	position: "relative",
	marginTop: 0,
	"& td": {
		padding: "0.25rem 1.25rem",
		fontSize: "0.875rem",
		color: "primary.dark",
		border: "none",
		borderBottom: "2px solid #E7E9EF",
		textAlign: "left",
		height: "32px",
		lineHeight: "22px",
		backgroundColor: "#FFFFFF",
	},
	backgroundColor: "#FFFFFF",
	cursor: "pointer",
	height: "37px",
});

type PtoTableBodyProps = {
	date: DateTime;
	onDoubleClickNurse: (nurseId: number) => void;
	selectedNurse?: Nurse | null;
};

export const PtoTableBody: FC<PtoTableBodyProps> = ({ date, onDoubleClickNurse, selectedNurse }) => {
	const { t } = useTranslation();
	const {
		getNurseFormattedName,
		chosenDepartment,
		isHospitalDemo,
		isHospitalView,
		updateNurse,
		getNurseById,
		addNewDBNurseShifts,
		removeNursesShiftsCompletely,
	} = useHospitalData();
	const showHospitalView = isHospitalView || isHospitalDemo;
	const { weekViewData, getPtoCost } = useWeekViewContext();
	const canViewWage = useRevealWage();
	const columnWidths = getStaffTableColumnWidths(showHospitalView);
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [selectedPreference, setSelectedPreference] = useState<{ preference: SpecificPreference; nurseId: number } | null>(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editingDayOff, setEditingDayOff] = useState<{ dayOff: DayOff; nurseId: number } | null>(null);
	const ptoTableRowId = useId();

	const [ptoToDelete, setPtoToDelete] = useState<{ dayOff: DayOff; nurseId: number } | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	const { deleteDayOffMutateAsync } = useDeleteDayOff({ addNewDBNurseShifts, removeNursesShiftsCompletely });

	const handleOpenDeleteDialog = useCallback(
		(preference: SpecificPreference, nurseId: number) => {
			const nurse = getNurseById(nurseId);
			if (!nurse) return;

			const daysOff = nursePreferencesToDaysOffAndPto(nurse);
			const dayOff = daysOff.find((d) => d.id === preference.id);

			if (dayOff) {
				setPtoToDelete({ dayOff, nurseId });
			}
		},
		[getNurseById],
	);

	const handleConfirmDelete = useCallback(
		async (restoreRecurringShifts: boolean, restoreTrimmedShifts: boolean) => {
			if (!ptoToDelete) return;
			setIsDeleting(true);
			try {
				await deleteDayOffMutateAsync({
					dayOff: ptoToDelete.dayOff,
					nurseId: ptoToDelete.nurseId,
					departmentId: chosenDepartment.department_id,
					restoreRecurringShifts,
					restoreTrimmedShifts,
				});
				const nurse = getNurseById(ptoToDelete.nurseId);
				if (nurse) {
					updateNurse({
						...nurse,
						employeeSpecificPreferences: nurse.employeeSpecificPreferences.filter((pref) => pref.id !== ptoToDelete.dayOff.id),
					});
				}
			} finally {
				setIsDeleting(false);
				setPtoToDelete(null);
			}
		},
		[ptoToDelete, deleteDayOffMutateAsync, chosenDepartment.department_id, getNurseById, updateNurse],
	);

	const handleCancelDelete = useCallback(() => {
		setPtoToDelete(null);
	}, []);

	const dayPto = useMemo<SpecificPreference[]>(() => weekViewData.find((d) => d.date.hasSame(date, "day"))?.pto ?? [], [weekViewData, date]);

	const ptoHoursByNurse = useMemo<Map<number, number>>(() => {
		const map = new Map<number, number>();
		for (const pref of dayPto) {
			map.set(pref.nurse_id, (map.get(pref.nurse_id) ?? 0) + pref.hours);
		}
		return map;
	}, [dayPto]);

	const preferenceForNurse = useCallback((nurseId: number): SpecificPreference | undefined => dayPto.find((pref) => pref.nurse_id === nurseId), [dayPto]);

	const formatPtoHours = useCallback(
		(nurse: Nurse): string => {
			// Exact-times (partial-day) PTO shows its window instead of a bare hours count (DAT-230).
			const pref = dayPto.find((p) => p.nurse_id === nurse.id);
			if (pref?.start_date && pref?.end_date && !isFullStaffingDayPto(pref.start_date, pref.end_date, chosenDepartment.dayStart)) {
				return formatPtoTimeRange(pref.start_date, pref.end_date);
			}
			return formatHoursMinutes(ptoHoursByNurse.get(nurse.id) ?? 0);
		},
		[dayPto, ptoHoursByNurse, chosenDepartment.dayStart],
	);

	const relevantPTONurses = useMemo<Nurse[]>(() => {
		const nurseIds = new Set<number>(dayPto.map((pref) => pref.nurse_id));
		return Array.from(nurseIds)
			.map((id) => getNurseById(id))
			.filter((n): n is Nurse => !!n && (!selectedNurse || n.id === selectedNurse.id));
	}, [dayPto, selectedNurse, getNurseById]);

	// Picking someone in the day's staff search narrows this list to them, so a non-empty list means the
	// PTO section is where they are — open it. Empty while nobody is picked, so the section returns to
	// however the user left it.
	const autoExpandedGroups = useMemo<ReadonlySet<string>>(
		() => (selectedNurse && relevantPTONurses.length > 0 ? new Set([PTO_GROUP_KEY]) : new Set()),
		[selectedNurse, relevantPTONurses.length],
	);
	const { expandedGroups, toggleGroupExpand } = useExpandedGroups({ autoExpandedGroups });
	const isCollapsed = !expandedGroups.has(PTO_GROUP_KEY);

	const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, preference: SpecificPreference, nurseId: number) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setSelectedPreference({ preference, nurseId });
	}, []);

	const handleMenuClose = useCallback(() => {
		setMenuAnchorEl(null);
		setSelectedPreference(null);
	}, []);

	const handleEditPTO = useCallback(
		(preference: SpecificPreference, nurseId: number) => {
			const nurse = getNurseById(nurseId);
			if (!nurse) return;

			const daysOff = nursePreferencesToDaysOffAndPto(nurse);
			const dayOff = daysOff.find((d) => d.id === preference.id);

			if (dayOff) {
				setEditingDayOff({ dayOff, nurseId });
				setEditDialogOpen(true);
			}
		},
		[getNurseById],
	);

	const handleSavePTO = useCallback(
		(updatedDayOff: DayOff) => {
			if (!editingDayOff) return;

			const nurse = getNurseById(editingDayOff.nurseId);
			if (!nurse) return;

			const allDaysOff = nursePreferencesToDaysOffAndPto(nurse);
			const updatedDaysOff = allDaysOff.map((d) => (d.id === updatedDayOff.id ? updatedDayOff : d));
			const updatedPreferences = daysOffAndPtoToNursePreferences(updatedDaysOff, nurse);
			updateNurse({ ...nurse, employeeSpecificPreferences: updatedPreferences });
			setEditDialogOpen(false);
			setEditingDayOff(null);
		},
		[editingDayOff, getNurseById, updateNurse],
	);

	const handleCloseEditDialog = useCallback(() => {
		setEditDialogOpen(false);
		setEditingDayOff(null);
	}, []);

	return (
		<>
			{!!relevantPTONurses.length && (
				<TableRow
					role="button"
					onClick={() => toggleGroupExpand(PTO_GROUP_KEY)}
					tabIndex={0}
					aria-expanded={!isCollapsed}
					aria-controls={ptoTableRowId}
					sx={{
						width: "100%",
						fontSize: "14px",
						color: "white",
						// primary.dark, not a fixed hex: every other section header in this table follows the
						// theme, so this one stayed navy when the user picked a different colour.
						backgroundColor: "primary.dark",
						border: "none",
						cursor: "pointer",
					}}
				>
					<TableCell colSpan={100} sx={{ px: 1.25, py: 0.5, fontSize: "14px", color: "white", bgcolor: "primary.dark", border: "none" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<Box sx={{ display: "flex", alignItems: "center" }}>{isCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}</Box>
							<Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
								<Typography component="span" sx={{ fontWeight: 600, width: "11rem" }}>
									{t("pto")}
								</Typography>
								<Typography component="span" sx={{ fontWeight: 500, width: "5rem", "&::before": { content: "'|'", mx: 0.5 } }}>
									<Box component="span" sx={{ mx: 1, fontWeight: 600 }}>
										{relevantPTONurses.length}
									</Box>
									staff
								</Typography>
							</Box>
						</Box>
					</TableCell>
				</TableRow>
			)}

			<TableRow>
				<TableCell colSpan={100} sx={{ padding: 0, border: "none" }}>
					<Collapse id={ptoTableRowId} in={!isCollapsed} timeout="auto" unmountOnExit>
						<Table sx={{ tableLayout: "fixed", width: "100%" }}>
							<TableBody>
								{relevantPTONurses.map((nurse) => {
									const cost = getPtoCost(date, nurse.id);
									const externalTimeOff = dayPto.find((pref) => pref.nurse_id === nurse.id && pref.external_time_off)?.external_time_off;

									return (
										<StyledTableRow key={`pto-table-row-${nurse.id}`} onDoubleClick={() => onDoubleClickNurse(nurse.id)}>
											{/*roles*/}
											<TableCell sx={{ width: columnWidths.role.width, minWidth: columnWidths.role.minWidth }}>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
														maxWidth: "100px",
														flexWrap: "wrap",
													}}
												>
													<ColorfulRole role={nurse.role} />
												</Box>
											</TableCell>
											{/* Name */}
											<TableCell sx={{ width: columnWidths.name.width, minWidth: columnWidths.name.minWidth }}>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														gap: 1,
														whiteSpace: "nowrap",
													}}
												>
													<Typography sx={{ fontSize: "14px" }}>{getNurseFormattedName(nurse.id)}</Typography>
												</Box>
											</TableCell>
											{/* Shift */}
											<TableCell sx={{ width: columnWidths.shift.width, minWidth: columnWidths.shift.minWidth }}>
												<Box sx={{ display: "flex" }}>
													<Box
														sx={{
															alignItems: "center",
															gap: 1,
															color: "primary.dark",
															opacity: 0.5,
															position: "relative",
															display: "flex",
															"&:hover .edit-icon": { opacity: 1 },
														}}
													>
														<Typography sx={{ fontSize: "14px" }}>{formatPtoHours(nurse)}</Typography>
													</Box>
												</Box>
											</TableCell>
											{/* Location */}
											<TableCell sx={{ width: columnWidths.location.width, minWidth: columnWidths.location.minWidth }} />
											{/* Wage */}
											<TableCell sx={{ width: showHospitalView ? "10%" : "12%", minWidth: "120px" }}>
												{!canViewWage ? (
													<Box sx={{ alignItems: "center", display: "flex", ml: 0.7 }} component="span">
														--
													</Box>
												) : (
													<InHouseTooltip title={"Counted only toward the total labor"}>
														<span style={{ paddingLeft: "5px", display: "flex", textAlign: "center" }}>{cost != null ? `$${cost.toFixed(2)}` : "-"}</span>
													</InHouseTooltip>
												)}
											</TableCell>
											{/* Notes */}
											<TableCell sx={{ width: showHospitalView ? "15%" : "20%", minWidth: "150px" }}>
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													<ExternalTimeOffIndicator externalTimeOff={externalTimeOff} />
													<Chip label={t("pto")} size="small" sx={{ bgcolor: "var(--inhouse-primary)", color: "white", fontWeight: "600" }} />
												</Box>
											</TableCell>
											{/* Action */}
											<TableCell sx={{ width: "6%", minWidth: "80px" }}>
												<Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", height: "100%", width: "100%" }}>
													<PtoRowActions
														onEdit={(event) => {
															event.stopPropagation();
															const preference = preferenceForNurse(nurse.id);
															if (preference) handleEditPTO(preference, nurse.id);
														}}
														onDelete={(event) => {
															event.stopPropagation();
															const preference = preferenceForNurse(nurse.id);
															if (preference) handleOpenDeleteDialog(preference, nurse.id);
														}}
														onMenuOpen={(event) => {
															const preference = preferenceForNurse(nurse.id);
															if (preference) handleMenuOpen(event, preference, nurse.id);
														}}
													/>
												</Box>
											</TableCell>
										</StyledTableRow>
									);
								})}
							</TableBody>
						</Table>
					</Collapse>
				</TableCell>
			</TableRow>

			{selectedPreference && (
				<PTOMenu
					preference={selectedPreference.preference}
					nurseId={selectedPreference.nurseId}
					actionsMenuAnchorEl={menuAnchorEl}
					onMenuClose={handleMenuClose}
					onEditPTO={handleEditPTO}
					onDeletePTO={handleOpenDeleteDialog}
				/>
			)}

			{editingDayOff && (
				<AddDayOffDialog
					open={editDialogOpen}
					onClose={handleCloseEditDialog}
					onSave={handleSavePTO}
					nurseId={editingDayOff.nurseId}
					dayOff={editingDayOff.dayOff}
				/>
			)}

			<DeletePTODialog
				dayOff={ptoToDelete?.dayOff ?? null}
				nurseId={ptoToDelete?.nurseId ?? null}
				isLoading={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>
		</>
	);
};
